// Screen ↔ hex. A cursor ray is intersected with the ground by refining against the hex's height a
// few times (terrain relief is small next to view distances), and hex centres project to CSS pixels
// through the ±WORLD_W copy nearest the camera.

import * as THREE from 'three/webgpu';
import { HEX_COUNT, hexAtWorld, hexCenter, hexCol, hexRow } from '../core/grid.ts';
import { wrapDelta } from './camera.ts';

const REFINE_STEPS = 3;

/** Re-pick under a still cursor at most this often while the camera moves. */
export const REPICK_MS = 100;

/**
 * Decides when the hex under the cursor must be looked up again. The cursor is remembered, so a
 * camera move under a still pointer (Fly To, minimap, keys, setPose) re-picks at the same pixel:
 *
 *   pointer move ---------------------> pick now
 *   camera pose changes ---> stale ---> pick once REPICK_MS passed since the last pick
 *                                       (so the resting pose always gets a final pick)
 */
export class HoverTracker {
  private pointer: { x: number; y: number } | null = null;
  private moving = false;
  private poseKey = '';
  private stale = false;
  private lastPickMs = -REPICK_MS;

  moved(x: number, y: number): void {
    this.pointer = { x, y };
    this.moving = true;
  }

  left(): void {
    this.pointer = null;
    this.moving = true;
  }

  /** The remembered cursor, client pixels, or null when it is off the view. */
  at(): { x: number; y: number } | null {
    return this.pointer;
  }

  /** Call once per frame with a key that changes with the camera pose; true when to re-pick. */
  frame(poseKey: string, nowMs: number): boolean {
    if (poseKey !== this.poseKey) {
      this.poseKey = poseKey;
      this.stale = true;
    }

    const cameraDue = this.stale && this.pointer !== null && nowMs - this.lastPickMs >= REPICK_MS;
    if (!this.moving && !cameraDue) {
      return false;
    }

    this.moving = false;
    this.stale = false;
    this.lastPickMs = nowMs;
    return true;
  }
}

export class Picker {
  private readonly ray = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();
  private readonly v = new THREE.Vector3();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly dom: HTMLElement;
  private readonly heightOf: (hex: number) => number;

  constructor(camera: THREE.PerspectiveCamera, dom: HTMLElement, heightOf: (hex: number) => number) {
    this.camera = camera;
    this.dom = dom;
    this.heightOf = heightOf;
  }

  /** Hex under a client pixel, or -1. */
  hexAt(clientX: number, clientY: number): number {
    const r = this.dom.getBoundingClientRect();
    this.ndc.set(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1);
    this.ray.setFromCamera(this.ndc, this.camera);
    const { origin, direction } = this.ray.ray;
    if (direction.y >= 0) {
      return -1;
    }
    let y = 0;
    let hex = -1;
    for (let i = 0; i < REFINE_STEPS; i++) {
      const t = (y - origin.y) / direction.y;
      const x = origin.x + direction.x * t;
      const z = origin.z + direction.z * t;
      hex = hexAtWorld(x, z);
      y = this.heightOf(hex);
    }
    return hex;
  }

  /** CSS-pixel position of a hex centre (relative to the view element) and whether it is on screen. */
  toScreen(hex: number, targetX: number): { x: number; y: number; visible: boolean } {
    if (hex < 0 || hex >= HEX_COUNT) {
      return { x: 0, y: 0, visible: false };
    }
    const c = hexCenter(hexCol(hex), hexRow(hex));
    const x = targetX + wrapDelta(targetX, c.x);
    this.v.set(x, this.heightOf(hex), c.z).project(this.camera);
    const w = this.dom.clientWidth;
    const h = this.dom.clientHeight;
    const visible = this.v.z > -1 && this.v.z < 1 && Math.abs(this.v.x) <= 1 && Math.abs(this.v.y) <= 1;
    return { x: (this.v.x * 0.5 + 0.5) * w, y: (-this.v.y * 0.5 + 0.5) * h, visible };
  }
}
